import React, { useState } from "react";
import {
  Image,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  ActivityIndicator,
} from "react-native";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { AppFontStyle } from "../../styles/AppFontStyle";
import CreateEventTooltip from "../home-common-components/CreateEventTooltip";
import { AppImages } from "../../utility/AppImages";
import { AppColors } from "../../utility/AppColors";
import * as ImagePicker from "expo-image-picker";
import DescriptionWalkthrough from "../walk-through-common-components/createEventTooltips/DescriptionWalkthrough";
import { toast } from "react-toastify"; // Assuming this is available, adjust for mobile if needed
import { uploadToS3 } from "../../services/authAPI";

const DescriptionAndImageComponent = ({
  isOpen,
  description,
  setDescription,
  setFileName,
  fileContent,
  setFileContent,
  beltsTooltip,
  type,
  scrollViewRef = null,
  durationAndQualityTooltip = false,
}) => {
  const deviceSize = useDeviceSize();
  const styles = useStyles();
  const [isUploading, setIsUploading] = useState(false); // Loader state

  const generateRandomString = (length = 6) => {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    const charactersLength = characters.length;

    while (result.length < length) {
      const randomIndex = Math.floor(Math.random() * charactersLength);
      result += characters.charAt(randomIndex);
    }
    return result;
  };

  const pickImage = async () => {
    let result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [4, 3],
      quality: 1,
    });

    if (!result.canceled) {
      const fileName = result.assets[0].fileName;
      const fileUri = result.assets[0].uri;

      setFileName(fileName);

      if (type === "update") {
        setIsUploading(true);
        const randomUsername = generateRandomString(6);
        const bodyParams = {
          bucketName: "squdifi-event-thumbnail",
          fileName: fileName,
          fileContent: fileUri,
          username: randomUsername,
        };

        try {
          const response = await uploadToS3(bodyParams);

          if (response.statusCode === 200 || response.statusCode === 201) {
            if (response.body) {
              const profilePicture = response.body.fileUrl;
              setFileContent(profilePicture);
            }
          } else {
            console.error("Upload failed with status:", response.statusCode);
            setFileContent(fileUri);
            toast.error("Failed to upload image");
          }
        } catch (error) {
          console.error("Error uploading to S3:", error);
          setFileContent(fileUri);
          toast.error(error.message || "Error uploading image");
        } finally {
          setIsUploading(false);
        }
      } else {
        setFileContent(fileUri);
      }
    }
  };

  return (
    <View
      style={[
        styles.mainInputWrapper,
        {
          position: "relative",
          zIndex:
            beltsTooltip || isOpen || durationAndQualityTooltip ? -1 : 100,
          elevation: 0,
        },
      ]}
    >
      {/* Description */}
      <View style={styles.leftWrapper}>
        <DescriptionWalkthrough scrollViewRef={scrollViewRef} />
        <View style={[styles.inputWrapper, { flex: 1, paddingHorizontal: 0 }]}>
          <View style={[styles.inputWrapper, { paddingHorizontal: 0 }]}>
            <TextInput
              placeholder={"Description & Rules"}
              multiline={true}
              placeholderTextColor={AppColors.appGrayColor_1C1C1C}
              style={[
                AppFontStyle.REGULAR_ANYBODY_12,
                styles.textInputStyle,
                {
                  verticalAlign: "top",
                  height: 105,
                },
              ]}
              onSubmitEditing={() => {}}
              onChangeText={setDescription}
              keyboardType="default"
              secureTextEntry={false}
              value={description}
            />
          </View>
        </View>
      </View>

      {/* Thumbnail */}
      <View style={styles.leftWrapper}>
        <Text style={[AppFontStyle.REGULAR_ANYBODY_12]}>Upload image</Text>
        <TouchableOpacity
          activeOpacity={0.8}
          style={{
            borderRadius: 15,
            backgroundColor: AppColors.appGrayColor_1C1C1C,
            justifyContent: "center",
          }}
          onPress={pickImage}
          disabled={isUploading}
        >
          <View style={styles.dragDropView}>
            {fileContent ? (
              <View>
                <Image
                  source={{ uri: fileContent }}
                  style={{
                    width: 182,
                    height: 79,
                    alignSelf: "center",
                    resizeMode: "contain",
                  }}
                />
              </View>
            ) : (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 15,
                }}
              >
                <Image
                  source={AppImages.icDragNDrop}
                  style={{
                    height: 40,
                    width: 40,
                    resizeMode: "contain",
                  }}
                />
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_12,
                    {
                      color: AppColors.gray_C3C3C3,
                      flexWrap: "wrap",
                    },
                  ]}
                  numberOfLines={3}
                >
                  Drag & drop file here
                </Text>
              </View>
            )}
            <TouchableOpacity
              style={{
                paddingHorizontal: 50,
                backgroundColor: AppColors.appViolentColor_6D08C3,
                justifyContent: "center",
                alignItems: "center",
                borderRadius: 10,
                paddingVertical: 10,
                marginTop: fileContent ? 10 : 0,
                flexDirection: "row",
                gap: 10,
              }}
              onPress={pickImage}
              disabled={isUploading}
            >
              {isUploading && (
                <ActivityIndicator size="small" color={AppColors.white} />
              )}
              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_12,
                  { color: AppColors.white },
                ]}
              >
                {isUploading ? "Uploading..." : "Browse"}
              </Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </View>
    </View>
  );
};

export default DescriptionAndImageComponent;

const useStyles = CreateResponsiveStyle(
  {
    mainInputWrapper: {
      flexDirection: "row",
      gap: 25,
    },
    leftWrapper: {
      flex: 0.5,
      flexDirection: "column",
      gap: 12,
    },
    dragDropView: {
      height: 105,
      backgroundColor: AppColors.appGrayColor_1C1C1C,
      borderRadius: 10,
      padding: 10,
      alignItems: "center",
      flexDirection: "row",
      justifyContent: "space-between",
      gap: 10,
    },
    inputWrapper: {
      backgroundColor: AppColors.appGrayColor_1C1C1C,
      paddingHorizontal: 12,
      borderRadius: 10,
    },
    textInputStyle: {
      paddingVertical: 18,
      paddingHorizontal: 10,
      color: AppColors.white,
      outlineStyle: "none",
    },
  },
  {
    [DEVICE_SIZES.LG]: {
      mainInputWrapper: {
        flexDirection: "row",
        gap: 25,
      },
      leftWrapper: {
        flex: 0.5,
        flexDirection: "column",
        gap: 12,
      },
    },
    [DEVICE_SIZES.MD]: {
      mainInputWrapper: {
        flexDirection: "column",
        gap: 20,
      },
      leftWrapper: {
        flex: 1,
        flexDirection: "column",
        gap: 12,
      },
    },
    [DEVICE_SIZES.SM]: {
      mainInputWrapper: {
        flexDirection: "column",
        gap: 20,
      },
      leftWrapper: {
        flex: 1,
        flexDirection: "column",
        gap: 12,
      },
      inputWrapper: {
        flex: 1,
      },
    },

    [DEVICE_SIZES.XS]: {
      mainInputWrapper: {
        flexDirection: "column",
        gap: 20,
      },
      leftWrapper: {
        flex: 1,
        flexDirection: "column",
        gap: 12,
      },
      inputWrapper: {
        flex: 1,
      },
    },
  }
);
